import { InjectProperty } from '@jack-henry/frontend-utils/di';
import {
    RemoteDepositCaptureService,
    RemoteDepositImage,
} from '@treasury/domain/remote-deposit-capture';
import { LabeledList, TmBaseComponent } from '@treasury/presentation';
import { moneyCheckIcon } from '@treasury/presentation/assets/icons';
import { tmIf } from '@treasury/utils/lit-helpers';
import { format } from 'date-fns';
import { PropertyValueMap, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { RemoteDepositItemVm } from '../view-models';
import { DepositVm } from '../view-models/deposit.vm';
import { ItemImageVm } from '../view-models/item-image.vm';

export const tagName = 'deposit-detail';

@customElement(tagName)
export class DepositDetail extends TmBaseComponent {
    @property({ attribute: false })
    public bottomSheetOpen = false;

    @property({ attribute: false })
    public deposit = {} as DepositVm;

    @InjectProperty()
    declare private readonly remoteDepositCaptureService: RemoteDepositCaptureService;

    @state()
    private checks?: RemoteDepositItemVm[];

    @property({ attribute: false })
    private selectedCheck?: RemoteDepositItemVm;

    @state()
    private selectedCheckImages?: ItemImageVm;

    @state()
    private depositEventsOpen = false;

    @state()
    private checkDetailOpen = false;

    protected async updated(
        _changedProperties: PropertyValueMap<never> | Map<PropertyKey, unknown>
    ) {
        if (_changedProperties.has('deposit') && this.deposit?.batchReference) {
            const response = await this.remoteDepositCaptureService.getItems(
                this.deposit.batchReference
            );
            this.checks =
                response?.map(item => new RemoteDepositItemVm(item, item.dataFields)) ?? [];
        }
        if (_changedProperties.has('selectedCheck')) {
            this.selectedCheckImages = new ItemImageVm(
                (await this.remoteDepositCaptureService.getItemImages(
                    this.selectedCheck?.itemReference as string
                )) as RemoteDepositImage
            );
        }
    }

    protected render() {
        if (!this.deposit) {
            return nothing;
        }
        return html`<tm-section class="pt-6 px-6">
                <div class="flex justify-between pb-2">
                    <div class="left">
                        <div class="text-xl">Deposit Total</div>
                        <div class="text-3xl font-light">${this.deposit.formattedTotalAmount}</div>
                    </div>
                    <div class="text-sm">
                        <div class="flex items-center">
                            <tm-icon
                                class="mr-1"
                                .icon=${this.deposit.statusIcon}
                                .color=${this.deposit.statusColor}
                            ></tm-icon>
                            ${this.deposit.statusDescription}
                        </div>
                    </div>
                </div>
                <tm-labeled-list
                    class="pb-4 border-b border-dashed"
                    .list=${new LabeledList(
                        {
                            depositDate: this.deposit.formattedDepositDate,
                            location: this.deposit.locationName,
                        },
                        ['location', 'depositDate']
                    )}
                ></tm-labeled-list>
                <tm-button
                    importance="secondary"
                    @click=${() => {
                        this.depositEventsOpen = true;
                    }}
                >
                    View Deposit Events
                </tm-button>
                <div class="py-4">
                    <h4 class="font-medium pb-2">
                        Checks <tm-badge>${this.deposit.totalCount}</tm-badge>
                    </h4>
                    ${this.renderChecks()}
                </div>
            </tm-section>

            <tm-bottom-sheet
                .open=${this.depositEventsOpen}
                @close=${() => (this.depositEventsOpen = false)}
            >
                <span slot="header-center">Deposit Events</span>
                <tm-section class="pt-6 px-6">
                    <div class="grid grid-cols-[auto_1fr] gap-x-3">
                        ${this.renderDepositEvents()}
                    </div>
                </tm-section>
            </tm-bottom-sheet>

            <tm-bottom-sheet
                .open=${this.checkDetailOpen}
                @close=${() => (this.checkDetailOpen = false)}
            >
                <span slot="header-center">Check Detail</span>
                <tm-section class="pt-6 px-6">${this.renderCheckDetail()}</tm-section>
            </tm-bottom-sheet>`;
    }

    private async openCheckDetail() {
        this.selectedCheckImages = new ItemImageVm(
            (await this.remoteDepositCaptureService.getItemImages(
                this.selectedCheck?.itemReference as string
            )) as RemoteDepositImage
        );

        this.checkDetailOpen = true;
    }

    private renderStatusIcon(check: RemoteDepositItemVm | undefined) {
        if (!check) {
            return nothing;
        }
        return html`<tm-icon
            class="mr-1"
            .icon=${check.statusIcon}
            .color=${`var(${check.statusColor}`}
        ></tm-icon>`;
    }

    private renderDepositEvents() {
        return this.deposit.events?.map(
            event =>
                html`<div class="text-left mr-3 py-1 border-b">
                        <span class="text-sm font-medium"
                            >${format(new Date(event.date), 'PP')}</span
                        ><span class="ml-1 text-xs text-secondary"
                            >${format(new Date(event.date), 'pp')}</span
                        >
                    </div>
                    <div class="text-sm text-right py-1">${event.description}</div>`
        );
    }

    private renderChecks() {
        return this.checks?.map(
            check =>
                html` <button
                    class="flex justify-between w-full py-2 text-sm"
                    @click=${() => {
                        this.selectedCheck = check;
                        this.openCheckDetail();
                    }}
                >
                    <div class="flex items-center text-left mr-3">
                        <div class="mr-1">${this.renderStatusIcon(check)}</div>
                        <div>
                            <div class="text-medium">${check.transactionReferenceNumber}</div>
                            <div class="text-xs" style="color: var(${check.statusColor})">
                                ${check.statusDescription}
                            </div>
                            <div ${tmIf(check.customerName)} class="text-xs ml-1">
                                (${check.customerName})
                            </div>
                        </div>
                    </div>
                    <div class="flex justify-end text-right items-center">
                        <tm-icon .icon=${moneyCheckIcon} class="mr-2"></tm-icon>
                        <span class="font-medium">
                            ${check.amount.toLocaleString('en-US', {
                                style: 'currency',
                                currency: 'USD',
                            })}
                        </span>
                    </div>
                </button>`
        );
    }

    private renderCheckDetail() {
        return html`<div class="flex justify-between pb-2">
                <div class="left">
                    <div class="text-3xl font-light">
                        ${this.selectedCheck?.amount.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD',
                        })}
                    </div>
                </div>
                <div class="text-sm">
                    <div class="flex items-center">
                        ${this.renderStatusIcon(this.selectedCheck)}
                        <div>${this.selectedCheck?.statusDescription}</div>
                    </div>
                </div>
            </div>
            <tm-labeled-list
                class="mb-4 pb-4 border-b border-dashed"
                .list=${new LabeledList(
                    {
                        dateAdded: this.selectedCheck?.formattedDate,
                        processingStatusDescription:
                            this.selectedCheck?.processingStatusDescription,
                        itemReference: this.selectedCheck?.itemReference,
                    },
                    ['dateAdded', 'processingStatusDescription', 'itemReference'],
                    {
                        itemReference: 'Reference Number',
                        processingStatusDescription: 'Processing Status',
                    }
                )}
            ></tm-labeled-list>
            <h4 class="pb-2 font-medium">Check Images</h4>
            <div class="my-2">
                <img
                    src="${ifDefined(this.selectedCheckImages?.frontImage)}"
                    alt="Front of check ${this.selectedCheckImages?.itemReference}"
                />
            </div>
            <div class="my-2">
                <img
                    src="${ifDefined(this.selectedCheckImages?.rearImage)}"
                    alt="Back of check ${this.selectedCheckImages?.itemReference}"
                />
            </div>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    position: relative;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: DepositDetail;
    }
}
